









  import { Component, mixins } from 'nuxt-property-decorator'
  import isUndefined from 'lodash/isUndefined'
  import isEqual from 'lodash/isEqual'
  import SeoMethodsMixin, { DIVIDER } from '../../mixins/SeoMethodsMixin'

  @Component({
    middleware: ['isAuthenticatedOrPlatformOpened'],
    watchQuery: ['q', 'category', 'filtersQuery', 'page', 'rows', 'sortCriteria', 'price'],
    components: {
      OneCatalogViewList: () => import('~/components/organisms/list/OneCatalogViewList.vue'),
    },
  })
  export default class SearchIndexView extends mixins<SeoMethodsMixin>(SeoMethodsMixin) {
    name: string = 'search-index-view'
    transition: string = ''
    mainComponentClass = 'one-search-page'

    mounted() {
      this.$plugins.onMounted(this.mainComponentClass, this)
    }

    destroyed() {
      this.$plugins.onUnmounted(this.mainComponentClass)
    }

    validate({ query }: any) {
      const { page, rows } = query
      // eslint-disable-next-line no-restricted-globals
      return !(isNaN(page) || isNaN(rows))
    }

    head() {
      return {
        title: this.searchQueryTitle,
        meta: [
          {
            hid: 'description',
            name: 'description',
            content: this.structuredSeoTitle(DIVIDER.DASH, this.searchSeoDescription),
          },
          { hid: 'robots', name: 'robots', content: 'noindex, follow' },
        ],
      }
    }

    get searchQueryTitle(): string | null {
      const { q } = this.$route.query
      return Array.isArray(q)
        ? this.structuredSeoTitle(DIVIDER.DOT, [q.join(DIVIDER.COMMA), this.filtersSeo])
        : (q && this.structuredSeoTitle(DIVIDER.DOT, [q, this.filtersSeo])) || null
    }

    get searchSeoDescription(): Array<string | undefined> {
      const search = this.$route.query.q
      return [`${this.$t('default_seo.search')} ${Array.isArray(search) ? search.join(DIVIDER.WHITESPACE) : search}`, this.filtersSeo, this.defaultSeoData.description]
    }

    get listingTitle(): string | null {
      const search = this.$route.query.q
      return (search && `${Array.isArray(search) ? search.join(DIVIDER.WHITESPACE) : search}`) || null
    }

    async fetch({
      store, route, app, redirect, from,
    }: any) {
      const currentPriceType: string = store.getters['layout/priceType']
      const addedPriceParam = (price: string | null) => {
        if (!(app.$utils.isAnonymousAndPurchaseDisabled || app.$utils.isB2BAndPlatformIsNotOpened) || !app.$utils.hasPricesEnabled || !price) {
          store.commit('products/SET_PRICE', price)
        }
      }
      const redirectDeletePrice = () => {
        const { query } = route
        delete query.price
        addedPriceParam(null)
        return redirect(301, route.path, query)
      }
      const validPrice = () => {
        const { price } = route.query
        if (price) {
          const parsedPrice: [number, number] | null = app.$routes.parsePriceString(price)
          if (!parsedPrice) {
            addedPriceParam(null)
            return redirectDeletePrice()
          }
          const [min, max] = parsedPrice
          if (min <= max && price.includes(currentPriceType)) {
            return addedPriceParam(price)
          }
          return redirectDeletePrice()
        }
        addedPriceParam(null)
        return null
      }
      const isRowsValid = (rows: number | null) : boolean => !!(rows && rows <= 45 && rows >= 15)
      const isPageValid = (page: number | null) : boolean => !!(page && page > 0)
      if (!isRowsValid(route.query.rows) || !isPageValid(route.query.page)) {
        return redirect(301, route.path, {
          ...route.query,
          ...{
            page: 1,
            rows: 15,
          },
        })
      }
      validPrice()
      store.commit('layout/TOGGLE_BREADCRUMBS', true)
      store.commit('products/SET_PAGE_SIZE', Number(route.query.rows))
      store.commit('products/SET_PAGE_NUMBER', Number(route.query.page))
      store.commit('products/SET_SORT_CRITERIA', route.query.sortCriteria)
      if (route.query.q) {
        let search = route.query.q
        if (Array.isArray(search)) {
          search = search.join(' ')
        }
        await store.dispatch('products/searchChanged', search)
        store.commit('layout/SET_BREADCRUMBS', [
          {
            name: app.i18n.t('search.query_breadcrumb', { searchString: search }),
          },
        ])
        // eslint-disable-next-line max-len
      } else if (route.query.category && store.state.categories.categories[route.query.category]) {
        const category = store.state.categories.categories[route.query.category]
        store.commit('layout/SET_BREADCRUMBS', [
          {
            name: app.i18n.t('search.category_breadcrumb', { categoryName: category.name }),
          },
        ])
      } else {
        store.commit('layout/SET_BREADCRUMBS', [
          {
            name: app.i18n.t('search.default_breadcrumb'),
          },
        ])
      }
      if (route.query.category && !!store.state.categories.categories[route.query.category]) {
        await store.dispatch('categories/selectCategory', route.query.category)
      }
      const queryWithoutCategory = {
        q: route.query.q,
        rows: route.query.rows,
        page: route.query.page,
        sortCriteria: route.query.sortCriteria,
        filtersQuery: route.query.filtersQuery,
        price: route.query.price,
      }
      const fetchSearchData = async () => {
        try {
          const setCatalogLoading = () => {
            if (process.client) {
              app.wait.start('catalog-loading')
            }
          }
          const clearCatalogLoading = () => {
            if (process.client) {
              setTimeout(() => {
                app.wait.end('catalog-loading')
              }, 250)
            }
          }
          setCatalogLoading()
          const products: { ids: Array<string>, categorySelected: boolean } = await store.dispatch('products/fetchProducts', route.query)
          if (products && products.categorySelected) {
            const { filtersQuery: oldFiltersFrom, q: qFrom } = from || { filtersQuery: undefined, q: undefined }
            const oldVal = { oldFiltersFrom, qFrom }

            const { filtersQuery: filtersQueryRoute, q: qRoute } = route
            const newVal = { filtersQueryRoute, qRoute }
            if (isUndefined(from) || !isEqual(newVal, oldVal)) {
              await store.dispatch('products/fetchFilters', route.query)
            }
          } else {
            store.commit('categories/CLEAR_SELECTED_CATEGORY', null)
            await store.dispatch('products/fetchFilters', queryWithoutCategory)
          }
          if (app.$utils.isPurchaseEnabledOrAuthenticated) {
            await store.dispatch('stocks/fetchStocksForProductsAndWarehouses', {
              skus: products.ids,
              warehouses: store.state.stocks.warehouses.allIds,
            })
            if (app.$utils.hasPricesEnabled) {
              await store.dispatch('products/fetchPricesForProducts', products.ids)
            }
          }
          clearCatalogLoading()
        } catch (error) {
          app.$logger.error(error)
        }
      }
      if (process.client) {
        fetchSearchData()
      } else {
        await fetchSearchData()
      }
      return Promise.resolve()
    }
  }
